import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Minus } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';

import { useUser } from '../context/UserContext';
import { createBiqConversationId, finalizeBiqAssistantContent, sendBiqAgenticMessage, signalBiqAgenticCancel, prefetchBiqPopupGreeting, peekBiqPopupGreeting, commitBiqPopupGreeting } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useBiqPageContext } from '../context/BiqPageContext';
import { useUsageTracking } from '../hooks/useUsageTracking';
import { USAGE_FEATURES } from '../constants/UsageLimits';
import { trackEvent, EVENTS } from '../utils/analytics';
import BiqComposer from './BiqComposer';

const LAUNCHER_SIZE = 60;
const PLACEMENT_GAP = 12;
const SIDE_HYSTERESIS = 12;
const DEFAULT_PANEL_HEIGHT = 523;
// Matches the messages list's fixed `h-[372px]`; only the header/footer are measured
// live (they're the parts that actually vary — composer growth, i18n line-wrapping).
const MESSAGES_LIST_HEIGHT = 372;
// Smallest list height still worth showing above/below the launcher before we fall
// back to pinning the panel to the full usable viewport height (P2).
const MIN_USABLE_LIST = 48;

// Picks which side of the (possibly dragged) launcher the panel opens on, and clamps
// it into the visible viewport. `prevSide` gives above/below hysteresis so the panel
// doesn't flip every frame when the launcher sits near the vertical midpoint.
// `naturalH` is the panel's unconstrained height (header + footer, measured live, plus
// the fixed message-list height) — never the shell's own (already-clamped) box, or a
// tight drag would ratchet the remembered height down and never recover it.
function computePlace(xv, yv, bounds, naturalH, chromeH, prevSide) {
  const isSm = bounds.width >= 640;
  const width = Math.min(isSm ? 460 : bounds.width - 32, bounds.right - bounds.left);

  const above = yv - PLACEMENT_GAP - bounds.top;
  const below = bounds.bottom - (yv + LAUNCHER_SIZE) - PLACEMENT_GAP;
  let side = prevSide === 'above' || prevSide === 'below' ? prevSide : (above >= below ? 'above' : 'below');
  const otherSpace = side === 'above' ? below : above;
  const currentSpace = side === 'above' ? above : below;
  if (otherSpace > currentSpace + SIDE_HYSTERESIS) {
    side = side === 'above' ? 'below' : 'above';
  }
  const chosenSpace = side === 'above' ? above : below;
  const usable = Math.max(0, bounds.bottom - bounds.top);

  let top;
  let h;
  let originY;
  if (chosenSpace >= chromeH) {
    // Enough room on the chosen side for at least a useful list: place fully above/below.
    const maxHeight = Math.max(chromeH, chosenSpace);
    h = Math.min(naturalH, maxHeight);
    top = side === 'above' ? yv - PLACEMENT_GAP - h : yv + LAUNCHER_SIZE + PLACEMENT_GAP;
    top = Math.min(Math.max(top, bounds.top), bounds.bottom - h);
    originY = side === 'above' ? 1 : 0;
  } else {
    // Too tight either way: pin to the full usable height. The panel may overlap the
    // launcher, but the messages list still scrolls, so nothing is unreachable.
    h = Math.min(naturalH, usable);
    top = bounds.top;
    originY = Math.min(Math.max((yv + LAUNCHER_SIZE / 2 - top) / (h || 1), 0), 1);
  }

  const leftA = xv;
  const leftB = xv + LAUNCHER_SIZE - width;
  const clampedA = Math.min(Math.max(leftA, bounds.left), bounds.right - width);
  const clampedB = Math.min(Math.max(leftB, bounds.left), bounds.right - width);
  const diffA = Math.abs(clampedA - leftA);
  const diffB = Math.abs(clampedB - leftB);
  const left = diffB <= diffA ? clampedB : clampedA;

  const originX = Math.min(Math.max((xv + LAUNCHER_SIZE / 2 - left) / width, 0), 1);

  return { left, top, width, maxHeight: h, originX, originY, side };
}

const BiQChatPopup = ({ isOpen, onClose, buttonRef, launcherX, launcherY, bounds }) => {
  const { t, i18n } = useTranslation();
  const { email, userDetails } = useUser();
  const { isDark } = useTheme();
  const { pageContext } = useBiqPageContext() || {};
  const [message, setMessage] = useState('');
  const [deepThinking, setDeepThinking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const popupRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  // Auto-scroll only follows the stream while the user is parked at the bottom.
  const autoScrollRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const abortControllerRef = useRef(null);
  const cancelTokenRef = useRef(null);
  const conversationIdRef = useRef(createBiqConversationId());
  const greetingCommittedRef = useRef(false);
  const inputRef = useRef(null);
  const restoreFocusRef = useRef(false);
  // Drag mode (launcherX/launcherY/bounds all provided) tracks the launcher's live
  // position; standalone usage (props absent) keeps the static fixed bottom-right panel.
  const hasDragProps = Boolean(launcherX && launcherY && bounds);
  const shellRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const sideRef = useRef(null);
  const [place, setPlace] = useState(null);
  const { trackUsage: trackBiqUsage } = useUsageTracking(USAGE_FEATURES.BIQ_QUERY);
  const {
    trackUsage: trackDeepThinkingUsage,
    isExceeded: isDeepThinkingExceeded,
  } = useUsageTracking(USAGE_FEATURES.BIQ_DEEP_THINKING);

  const styles = {
    popupBg: isDark ? '#1d1d1d' : '#ffffff',
    popupBorder: isDark ? '#282a29' : '#C3C4C3',
    headerBg: isDark ? '#171717' : '#FFFFFF',
    headerBorder: isDark ? '#282a29' : '#E7EBE8',
    // Transparent so the body inherits popupBg and the modal reads as one surface
    messagesBg: 'transparent',
    inputAreaBg: isDark ? '#1d1d1d' : '#FFFFFF',
    headerIconBg: isDark ? 'rgba(21,203,94,0.16)' : '#DCFCE7',
    timestampText: isDark ? '#5d625f' : '#828382',
    biqBubbleBg: isDark ? '#282a29' : '#E8EDEA',
    biqText: isDark ? '#f0f5f2' : '#1d1d1d',
    mutedText: isDark ? '#5d625f' : '#828382',
  };

  // Close popup when clicking outside (but not when clicking the toggle button)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsidePopup = popupRef.current && popupRef.current.contains(event.target);
      const isClickOnButton = buttonRef?.current && buttonRef.current.contains(event.target);
      
      if (!isClickInsidePopup && !isClickOnButton) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('pointerdown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        // Only pull focus back to the launcher if it was inside the panel.
        restoreFocusRef.current = !!popupRef.current?.contains(document.activeElement);
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Return focus to the launcher when the dialog is dismissed from inside it.
  useEffect(() => {
    if (isOpen || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    buttonRef?.current?.focus({ preventScroll: true });
  }, [isOpen, buttonRef]);

  // Live placement while dragging: recomputes above/below + left/right clamping from the
  // launcher's motion values, rAF-coalesced so a drag never re-renders the dashboard (only
  // this component's `place` state updates). The initial call is synchronous (useLayoutEffect)
  // so the panel doesn't flash at (0,0) the first frame it opens. Only the header and footer
  // are observed (never the shell — its height is already maxHeight-clamped, so feeding that
  // back in would ratchet the panel shorter every time it opens somewhere tight).
  useLayoutEffect(() => {
    if (!isOpen || !hasDragProps) return undefined;
    let raf = 0;
    // Header/footer heights are cached here and only refreshed by the ResizeObserver —
    // `compute` runs on every launcher-drag frame and must never force a layout read.
    let headerH = 0;
    let footerH = 0;
    const measure = () => {
      headerH = headerRef.current?.offsetHeight || 0;
      footerH = footerRef.current?.offsetHeight || 0;
    };
    const compute = () => {
      raf = 0;
      const borderH = isDark ? 2 : 0;
      const naturalH = headerH + footerH + MESSAGES_LIST_HEIGHT + borderH;
      const chromeH = headerH + footerH + MIN_USABLE_LIST;
      const next = computePlace(launcherX.get(), launcherY.get(), bounds, naturalH, chromeH, sideRef.current);
      sideRef.current = next.side;
      setPlace(next);
    };
    const scheduleCompute = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };
    measure();
    compute();

    const ro = new ResizeObserver(() => {
      measure();
      scheduleCompute();
    });
    if (headerRef.current) ro.observe(headerRef.current);
    if (footerRef.current) ro.observe(footerRef.current);

    const unsubX = launcherX.on('change', scheduleCompute);
    const unsubY = launcherY.on('change', scheduleCompute);
    return () => {
      unsubX();
      unsubY();
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isOpen, hasDragProps, bounds, launcherX, launcherY, isDark]);

  const firstName = (userDetails?.name || '').trim();
  const welcomeText = firstName
    ? t('biq.popup.welcome', { name: firstName })
    : t('biq.popup.welcomeNoName');

  const welcomeMessage = (content = welcomeText) => ({
    id: 'welcome',
    type: 'biq',
    content,
    timestamp: new Date(),
  });

  const resetThread = () => {
    conversationIdRef.current = createBiqConversationId();
    setMessages([welcomeMessage()]);
    setMessage('');
    autoScrollRef.current = true;
    lastScrollTopRef.current = 0;
    setIsStreaming(false);
    setIsLoading(false);
    if (abortControllerRef.current) {
      if (cancelTokenRef.current) {
        void signalBiqAgenticCancel(cancelTokenRef.current);
      }
      cancelTokenRef.current = null;
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const pageLabel = pageContext?.page_label || 'this page';
  const pageId = pageContext?.page_id || '';
  const openCommodity = pageContext?.commodity || '';
  // Compact facts already on the page (Account WhatsApp, Usage meters, Upgrade promo).
  const pageFacts = useMemo(() => {
    if (!pageContext || typeof pageContext !== 'object') return undefined;
    const out = {};
    if (typeof pageContext.whatsapp_linked === 'boolean') {
      out.whatsapp_linked = pageContext.whatsapp_linked;
    }
    if (typeof pageContext.is_promotional === 'boolean') {
      out.is_promotional = pageContext.is_promotional;
    }
    if (typeof pageContext.tier === 'string' && pageContext.tier) {
      out.tier = pageContext.tier;
    }
    if (typeof pageContext.expires_at === 'string' && pageContext.expires_at) {
      out.expires_at = pageContext.expires_at;
    }
    if (typeof pageContext.reset_in_days === 'number') {
      out.reset_in_days = pageContext.reset_in_days;
    }
    // Usage: pick the busiest non-unlimited meter for a gentle callout in the hello.
    const features = pageContext.features;
    if (features && typeof features === 'object') {
      let bestKey = null;
      let bestPct = -1;
      for (const [key, row] of Object.entries(features)) {
        if (!row || typeof row !== 'object' || row.is_unlimited) continue;
        const pct = Number(row.percentage);
        if (!Number.isFinite(pct) || pct < bestPct) continue;
        bestPct = pct;
        bestKey = key;
      }
      if (bestKey && bestPct >= 40) {
        out.near_limit_feature = bestKey;
        out.near_limit_pct = Math.round(bestPct);
      }
    }
    return Object.keys(out).length ? out : undefined;
  }, [pageContext]);

  // Prefetch as soon as the page/commodity is known — before the bubble opens.
  useEffect(() => {
    if (!email) return;
    void prefetchBiqPopupGreeting({
      pageLabel,
      pageId,
      openCommodity,
      language: i18n.language,
      pageFacts,
    });
  }, [email, pageLabel, pageId, openCommodity, i18n.language, pageFacts]);

  // Open: cached hello, or three-dots until prefetch lands. Close: new thread.
  useEffect(() => {
    if (isOpen) {
      const cached = peekBiqPopupGreeting();
      setMessages((prev) =>
        prev.length > 1 || (prev[0] && prev[0].id !== 'welcome')
          ? prev
          : [welcomeMessage(cached || '')]
      );
      if (cached && !greetingCommittedRef.current) {
        greetingCommittedRef.current = true;
        void commitBiqPopupGreeting({
          conversationId: conversationIdRef.current,
          greeting: cached,
          pageLabel,
        });
      }
    } else {
      // Reset messages and thread id when popup closes (new thread on next open)
      conversationIdRef.current = createBiqConversationId();
      greetingCommittedRef.current = false;
      setMessages([]);
      setMessage('');
      autoScrollRef.current = true;
      lastScrollTopRef.current = 0;
      setIsStreaming(false);
      setIsLoading(false);
      if (abortControllerRef.current) {
        if (cancelTokenRef.current) {
          void signalBiqAgenticCancel(cancelTokenRef.current);
        }
        cancelTokenRef.current = null;
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, i18n.language, pageLabel]);

  // Prefetch done after open: hello, or i18n if empty. Persist generated only.
  useEffect(() => {
    if (!isOpen || greetingCommittedRef.current) return;
    let cancelled = false;
    (async () => {
      const generated = await prefetchBiqPopupGreeting({
        pageLabel,
        pageId,
        openCommodity,
        language: i18n.language,
        pageFacts,
      });
      if (cancelled) return;
      const content = generated || welcomeText;
      setMessages((prev) =>
        prev.length > 1 || (prev[0] && prev[0].id !== 'welcome')
          ? prev
          : [welcomeMessage(content)]
      );
      if (generated && !greetingCommittedRef.current) {
        greetingCommittedRef.current = true;
        void commitBiqPopupGreeting({
          conversationId: conversationIdRef.current,
          greeting: generated,
          pageLabel,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, pageLabel, pageId, openCommodity, i18n.language, welcomeText, pageFacts]);

  // Auto-scroll to bottom when messages change, unless the user scrolled up to read
  useEffect(() => {
    if (!autoScrollRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  // Programmatic scrolls only ever move downwards, so any upward movement is the
  // user's own. Returning to within a few pixels of the bottom re-arms following.
  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const scrolledUp = el.scrollTop < lastScrollTopRef.current - 1;
    lastScrollTopRef.current = el.scrollTop;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 48;
    if (atBottom) autoScrollRef.current = true;
    else if (scrolledUp) autoScrollRef.current = false;
  };

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        if (cancelTokenRef.current) {
          void signalBiqAgenticCancel(cancelTokenRef.current);
        }
        cancelTokenRef.current = null;
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle send message
  const handleSendMessage = async () => {
    if (!message.trim() || !email || isLoading || isStreaming) return;

    // A fresh send always jumps back to the bottom, whatever the user was reading.
    autoScrollRef.current = true;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [
      ...prev.map((m) =>
        m.id === 'welcome' && !m.content ? { ...m, content: welcomeText } : m
      ),
      userMessage,
    ]);
    setMessage('');
    setIsLoading(true);
    setIsStreaming(true);
    const useDeepThinking = deepThinking && !isDeepThinkingExceeded;
    if (deepThinking && isDeepThinkingExceeded) {
      setDeepThinking(false);
    }
    if (useDeepThinking) {
      trackDeepThinkingUsage().catch(() => {});
    } else {
      trackBiqUsage().catch(() => {});
    }

    // Create placeholder for streaming response
    const biqMessageId = Date.now() + 1;
    const biqMessage = {
      id: biqMessageId,
      type: 'biq',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };

    setMessages((prev) => [...prev, biqMessage]);

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    try {
      const chatResult = await sendBiqAgenticMessage(
        email,
        userMessage.content,
        {
          conversationId: conversationIdRef.current,
          deepThinking: useDeepThinking,
          language: i18n.language,
          pageContext,
          onCancelToken: (t) => {
            cancelTokenRef.current = t;
          },
        },
        // onStatus - optional status updates
        (event) => {
          // Status updates can be shown if needed
        },
        // onTextDelta - stream text tokens
        (delta, isReset) => {
          setMessages((prev) => {
            const newMessages = [...prev];
            const msgIndex = newMessages.findIndex((m) => m.id === biqMessageId);
            if (msgIndex !== -1) {
              newMessages[msgIndex] = {
                ...newMessages[msgIndex],
                content: isReset ? delta : newMessages[msgIndex].content + delta,
              };
            }
            return newMessages;
          });
        },
        // onComplete - mark streaming as done
        (event) => {
          if (event?.conversation_id) {
            conversationIdRef.current = event.conversation_id;
          }
          setMessages((prev) => {
            const newMessages = [...prev];
            const msgIndex = newMessages.findIndex((m) => m.id === biqMessageId);
            if (msgIndex !== -1) {
              const streamed = newMessages[msgIndex].content;
              const nextContent = finalizeBiqAssistantContent(streamed, event);
              newMessages[msgIndex] = {
                ...newMessages[msgIndex],
                content: nextContent,
                isStreaming: false,
              };
            }
            return newMessages;
          });
          setIsStreaming(false);
        },
        // onChartReady - handle charts if needed (optional for popup)
        null,
        // onPdfReady - not used in popup (yet)
        null,
        // onError - handle errors gracefully
        (event) => {
          setMessages((prev) => {
            const newMessages = [...prev];
            const msgIndex = newMessages.findIndex((m) => m.id === biqMessageId);
            if (msgIndex !== -1) {
              newMessages[msgIndex] = {
                ...newMessages[msgIndex],
                content: event.message || t('biq.errorProcessing'),
                isStreaming: false,
              };
            }
            return newMessages;
          });
          setIsStreaming(false);
        },
        abortControllerRef.current.signal
      );
      if (chatResult?.conversationId) {
        conversationIdRef.current = chatResult.conversationId;
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('BiQ error:', error);
        setMessages((prev) => {
          const newMessages = [...prev];
          const msgIndex = newMessages.findIndex((m) => m.id === biqMessageId);
          if (msgIndex !== -1) {
            newMessages[msgIndex] = {
              ...newMessages[msgIndex],
              content: t('biq.errorGeneral'),
              isStreaming: false,
            };
          }
          return newMessages;
        });
      }
      setIsStreaming(false);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      cancelTokenRef.current = null;
    }
  };

  // Enter sends; Shift+Enter inserts a newline. Skip while IME is composing.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleStopStreaming = () => {
    const cancelToken = cancelTokenRef.current;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    if (cancelToken) {
      void signalBiqAgenticCancel(cancelToken);
    }
    cancelTokenRef.current = null;
    setIsStreaming(false);
    setIsLoading(false);
    setMessages((prev) =>
      prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg))
    );
  };

  return (
    // Reduced motion falls back to a plain fade (no scale) instead of framer's "never animate" default.
    <MotionConfig reducedMotion="user">
      {/* Panel stays mounted so the close effect's setMessages([]) can't race a frozen exit
          snapshot; interaction is gated on this always-rendered ancestor via `inert` instead. */}
      <div className="contents" inert={isOpen ? undefined : ''}>
        <AnimatePresence initial={false}>
          {isOpen && (
            // Drag mode: an unanimated shell carries the drag-aware left/top/width/maxHeight
            // (measured off the launcher's motion values); the spring below only ever animates
            // opacity/scale on an unscaled box. Standalone mode: the shell is `contents` (a
            // no-op) and the panel keeps its old static fixed bottom-right classes.
            <div
              key="biq-panel"
              ref={shellRef}
              className={hasDragProps ? 'fixed z-[2001]' : 'contents'}
              style={
                hasDragProps
                  ? {
                      left: place?.left ?? 0,
                      top: place?.top ?? 0,
                      width: place?.width ?? 358,
                      maxHeight: place?.maxHeight ?? DEFAULT_PANEL_HEIGHT,
                    }
                  : undefined
              }
            >
            <motion.div
              id="biq-panel"
              ref={popupRef}
              role="dialog"
              aria-label={t('biq.popup.assistant')}
              initial={{ opacity: 0, scale: 0.2 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.2, transition: { duration: 0.18, ease: 'easeIn' } }}
              transition={{
                default: { type: 'spring', stiffness: 420, damping: 24, mass: 0.9 },
                opacity: { duration: 0.15, ease: 'easeOut' },
              }}
              style={
                hasDragProps
                  ? { originX: place?.originX ?? 1, originY: place?.originY ?? 1, backgroundColor: styles.popupBg }
                  : { originX: 1, originY: 1, backgroundColor: styles.popupBg }
              }
              className={
                hasDragProps
                  ? `w-full max-h-[inherit] flex flex-col rounded-[20px] shadow-2xl overflow-visible ${
                      isDark ? 'border border-[#282a29]' : ''
                    }`
                  : `fixed bottom-[56px] sm:bottom-[74px] right-[16px] sm:right-[32px] w-[calc(100vw-32px)] sm:w-[min(460px,calc(100vw-64px))] max-h-[calc(100svh-72px)] sm:max-h-[calc(100svh-98px)] flex flex-col rounded-[20px] shadow-2xl z-[2001] overflow-visible ${
                      isDark ? 'border border-[#282a29]' : ''
                    }`
              }
            >
      {/* Header */}
      <div
        ref={headerRef}
        className="px-[16px] py-[14px] flex items-center justify-between border-b rounded-t-[20px] shrink-0"
        style={{ backgroundColor: styles.headerBg, borderColor: styles.headerBorder }}
      >
        <div className="flex items-center gap-[12px]">
          <div
            className="w-[40px] h-[40px] flex-shrink-0 rounded-[11px] flex items-center justify-center"
            style={{ backgroundColor: styles.headerIconBg }}
          >
            <MessageSquare size={20} strokeWidth={1.75} className="text-[#16A34A]" />
          </div>
          <div>
            <h3
              className="font-['Inter'] font-extrabold text-[16px]"
              style={{ color: styles.biqText }}
            >
              {t('biq.popup.assistant')}
            </h3>
            <div className="flex items-center gap-[6px]">
              <span className="w-[8px] h-[8px] bg-bloom-green rounded-full animate-pulse"></span>
              <span className="font-['Inter'] text-[12px]" style={{ color: styles.mutedText }}>
                {t('biq.popup.online')}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            restoreFocusRef.current = true;
            onClose();
          }}
          className="w-[32px] h-[32px] rounded-full flex items-center justify-center bg-transparent transition-colors"
          aria-label={t('biq.popup.minimizeChat')}
        >
          <Minus size={18} strokeWidth={2.25} className={isDark ? 'text-[#f0f5f2]' : 'text-[#444746]'} />
        </button>
      </div>

      {/* Chat Messages Area */}
      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        className="h-[372px] min-h-0 overflow-y-auto overflow-x-hidden p-[16px]"
        style={{ backgroundColor: styles.messagesBg }}
      >
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-[10px] mb-[16px] ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}
          >
            {msg.type === 'biq' && (
              <img
                src="/assets/logo.png"
                alt="BiQ"
                className="w-[25px] h-[34px] flex-shrink-0 object-contain"
              />
            )}
            {msg.type === 'user' && (
              <div className="w-[32px] h-[32px] bg-[#5d625f] rounded-full flex-shrink-0 flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="#1d1d1d"/>
                  <path d="M12.0002 14.5C6.99016 14.5 2.91016 17.86 2.91016 22C2.91016 22.28 3.13016 22.5 3.41016 22.5H20.5902C20.8702 22.5 21.0902 22.28 21.0902 22C21.0902 17.86 17.0102 14.5 12.0002 14.5Z" fill="#1d1d1d"/>
                </svg>
              </div>
            )}
            <div className={`flex-1 min-w-0 ${msg.type === 'user' ? 'flex justify-end' : ''}`}>
              <div
                className={`rounded-[12px] p-[12px] max-w-full min-w-0 wrap-anywhere ${
                  msg.type === 'user' ? 'bg-bloom-green rounded-tr-[4px]' : 'w-fit rounded-tl-[4px]'
                }`}
                style={{
                  ...(msg.type === 'biq' ? { backgroundColor: styles.biqBubbleBg } : null),
                }}
              >
                {msg.type === 'biq' ? (
                  <div
                    className="font-['Inter'] text-[14px] leading-relaxed"
                    style={{ color: styles.biqText }}
                  >
                    {!msg.content ? (
                      <div className="flex gap-1.5">
                        <span className="w-2 h-2 bg-bloom-green rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-bloom-green rounded-full animate-bounce" style={{ animationDelay: '200ms' }} />
                        <span className="w-2 h-2 bg-bloom-green rounded-full animate-bounce" style={{ animationDelay: '400ms' }} />
                      </div>
                    ) : (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                          strong: ({ node, ...props }) => <strong className="font-semibold text-bloom-green" {...props} />,
                          em: ({ node, ...props }) => <em className="italic" {...props} />,
                          // react-markdown 9 dropped the `inline` prop; fenced code now renders via `pre` below.
                          code: ({ node, className = '', ...props }) => (
                            <code className={`px-1.5 py-0.5 rounded text-bloom-green text-xs font-mono bg-[#F3F4F6] dark:bg-[#1d1d1d] ${className}`} {...props} />
                          ),
                          pre: ({ node, ...props }) => (
                            <pre className="mb-2 max-w-full rounded p-2 text-xs font-mono whitespace-pre-wrap wrap-anywhere bg-[#F3F4F6] dark:bg-[#1d1d1d] [&>code]:p-0 [&>code]:bg-transparent [&>code]:text-inherit" {...props} />
                          ),
                          // Cells wrap at spaces so numbers stay whole; overflow-x-auto is only the last-resort fallback.
                          table: ({ node, ...props }) => (
                            <div className="mb-2 max-w-full overflow-x-auto wrap-normal" tabIndex={0}>
                              <table className="w-full border-collapse [&_th]:px-2 [&_th]:py-1 [&_td]:px-2 [&_td]:py-1 [&_th]:text-left [&_th]:align-top [&_td]:align-top [&_th]:border-b [&_td]:border-b [&_th]:border-current/15 [&_td]:border-current/10 [&_th]:whitespace-normal [&_td]:whitespace-normal" {...props} />
                            </div>
                          ),
                          ul: ({ node, ...props }) => <ul className="list-disc pl-4 mb-2 space-y-1" {...props} />,
                          ol: ({ node, ...props }) => <ol className="list-decimal pl-4 mb-2 space-y-1" {...props} />,
                          li: ({ node, ...props }) => <li {...props} />,
                          a: ({ node, ...props }) => (
                            <a className="text-bloom-green underline hover:text-bloom-green-hover" target="_blank" rel="noopener noreferrer" {...props} />
                          ),
                        }}
                      >
                        {msg.content || ''}
                      </ReactMarkdown>
                    )}
                  </div>
                ) : (
                  <p className="font-['Inter'] text-[14px] text-[#1d1d1d] leading-relaxed">
                    {msg.content}
                  </p>
                )}
              </div>
              {msg.type === 'biq' && (
                <span
                  className="font-['Inter'] text-[11px] mt-[6px] block"
                  style={{ color: styles.timestampText }}
                >
                  BiQ • {msg.id === 'welcome' ? t('biq.popup.justNow') : new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div ref={footerRef} className="relative rounded-b-[20px] px-[12px] pt-[12px] pb-[14px] shrink-0" style={{ backgroundColor: styles.inputAreaBg }}>
        <BiqComposer
          inputRef={inputRef}
          inputValue={message}
          onInputChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onSend={handleSendMessage}
          onStop={handleStopStreaming}
          isStreaming={isStreaming}
          loading={isLoading}
          deepThinking={deepThinking}
          onDeepThinkingChange={(enabled) => {
            if (enabled && isDeepThinkingExceeded) return;
            trackEvent(EVENTS.DEEP_THINKING_TOGGLED, { enabled, surface: 'popup' });
            setDeepThinking(enabled);
          }}
          conversationActive
          compact
          placeholder={t('biq.popup.typeMessage')}
          onPlusClick={resetThread}
          disabled={!email}
          maxGrow={128}
        />
      </div>
            </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  );
};

export default BiQChatPopup;

